import { getApiUrl } from "@/utils/api";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { resolveImageUrl } from "@/utils/imageUrl";
import axios from "axios";

const API = getApiUrl();

export const EnquiryForm = ({ propertyId, propertyTitle, propertyPrice, propertyAddress, agentId, agentName, agentLogo, onSuccess }) => {
  const buildDefaultMessage = (title, price, address) => {
    if (!title) return "";
    const bits = [`I'm interested in "${title}"`];
    if (price) bits.push(`priced at ${price}`);
    if (address) bits.push(`(${address})`);
    return `${bits.join(" ")}. Please get in touch to arrange a viewing.`;
  };

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: buildDefaultMessage(propertyTitle, propertyPrice, propertyAddress),
  });

  // Keep the prefilled message in sync when the caller swaps `propertyTitle`
  // (e.g. modal reopened for a different property). Skip once the user has
  // typed their own text.
  useEffect(() => {
    setFormData((f) => {
      const defaultForCurrent = buildDefaultMessage(propertyTitle, propertyPrice, propertyAddress);
      const stillDefault = !f.message || /^I'm interested in ".+"[^]*Please get in touch to arrange a viewing\.$/.test(f.message);
      if (!stillDefault) return f;
      return { ...f, message: defaultForCurrent };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyTitle, propertyPrice, propertyAddress]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill in your name, phone number and email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/enquiry`, {
        ...formData,
        property_id: propertyId || null,
        property_title: propertyTitle || null,
        agent_id: agentId || null,
        agent_name: agentName || null,
      });

      if (response.data.status === "success" || response.data.status === "partial") {
        toast.success("Enquiry sent successfully! We'll be in touch soon.");
        setFormData({ name: "", email: "", phone: "", message: "" });
        if (onSuccess) onSuccess();
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error sending enquiry:", error);
      toast.error("Failed to send enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="enquiry-form">
      {/* Agent Logo */}
      {agentLogo && (
        <div className="flex items-center gap-3 pb-4 border-b border-border">
          <img 
            src={resolveImageUrl(agentLogo)} 
            alt={agentName || "Agent"} 
            className="h-12 w-auto max-w-[100px] object-contain slh-no-img-style"
          />
          {agentName && (
            <div>
              <p className="text-sm text-muted-foreground">Enquiry handled by</p>
              <p className="font-medium">{agentName}</p>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="name" className="text-sm font-medium">
          Name <span className="text-accent">*</span>
        </Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Your full name"
          className="h-12 rounded-none border-border bg-white/50 focus:ring-1 focus:ring-primary focus:border-primary"
          required
          data-testid="enquiry-name-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm font-medium">
          Email <span className="text-accent">*</span>
        </Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="your@email.com"
          className="h-12 rounded-none border-border bg-white/50 focus:ring-1 focus:ring-primary focus:border-primary"
          required
          data-testid="enquiry-email-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone" className="text-sm font-medium">
          Phone <span className="text-accent">*</span>
        </Label>
        <Input
          id="phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          placeholder="+1 (234) 567-890"
          className="h-12 rounded-none border-border bg-white/50 focus:ring-1 focus:ring-primary focus:border-primary"
          required
          data-testid="enquiry-phone-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-sm font-medium">
          Message
        </Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us what you're looking for..."
          className="min-h-32 rounded-none border-border bg-white/50 focus:ring-1 focus:ring-primary focus:border-primary resize-none"
          data-testid="enquiry-message-input"
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-14 bg-primary text-white hover:bg-primary/90 rounded-none text-base font-medium btn-lift"
        data-testid="enquiry-submit-btn"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          "Send Enquiry"
        )}
      </Button>
    </form>
  );
};

export default EnquiryForm;
